import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ChefHat, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Login() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(email, password);
      } else {
        await register({ email, password, name, unit, role: 'resident' });
      }
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(' ') : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8" data-testid="login-logo">
          <div className="w-10 h-10 rounded-xl bg-[#E05A47] flex items-center justify-center">
            <ChefHat className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-bold text-xl tracking-tight text-[#222] font-[Manrope]">My Executive Chef</span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#EBEBEB] p-8 shadow-sm">
          {/* Tabs */}
          <div className="flex rounded-lg bg-[#F7F7F7] p-1 mb-6">
            {['login', 'register'].map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setError(''); }}
                data-testid={`auth-tab-${t}`}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${tab === t ? 'bg-white text-[#222] shadow-sm' : 'text-[#717171]'}`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" data-testid="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {tab === 'register' && (
              <>
                <div>
                  <Label className="text-[#222] font-medium text-sm">Full Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} required placeholder="Your name" className="mt-1.5 bg-white border-[#EBEBEB] focus:ring-[#E05A47] focus:border-transparent rounded-lg h-11" data-testid="register-name-input" />
                </div>
                <div>
                  <Label className="text-[#222] font-medium text-sm">Unit Number</Label>
                  <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="e.g. PH-2401" className="mt-1.5 bg-white border-[#EBEBEB] focus:ring-[#E05A47] focus:border-transparent rounded-lg h-11" data-testid="register-unit-input" />
                </div>
              </>
            )}
            <div>
              <Label className="text-[#222] font-medium text-sm">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" className="mt-1.5 bg-white border-[#EBEBEB] focus:ring-[#E05A47] focus:border-transparent rounded-lg h-11" data-testid="email-input" />
            </div>
            <div>
              <Label className="text-[#222] font-medium text-sm">Password</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Min 6 characters" className="bg-white border-[#EBEBEB] focus:ring-[#E05A47] focus:border-transparent rounded-lg h-11 pr-10" data-testid="password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#717171]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold h-11 btn-press" data-testid="auth-submit-btn">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : tab === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#717171] mt-6">
            Demo resident: <span className="font-medium text-[#222]">melissa@demo.com / resident123</span>
          </p>
        </div>
      </div>
    </div>
  );
}
